import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    VStack,
    HStack,
    Input,
    Text,
    Box,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    FormControl,
    FormLabel,
    Badge,
} from '@chakra-ui/react';
import SurveyPreview from './SurveyPreview';
import InfoMessage from './shared/InfoMessage';

/**
 * Replace [firstname] variable in invitation text
 */
function replaceInvitationVariables(template, name) {
    if (!template) return '';

    const firstName = name ? name.split(' ')[0] : 'there';
    return template.replace(/\[firstname\]/gi, firstName);
}

/**
 * Generate survey link preview
 */
function generateSurveyLink(pollId, phone) {
    // This is just a preview - real link would come from backend
    return `surveylink.com/${pollId || 'xxx'}`;
}

function PreviewPollModal({ isOpen, onClose, poll, questions, invitationText, prologueOverride }) {
    const [testName, setTestName] = useState('John Smith');
    const [testPhone, setTestPhone] = useState('+15551234567');
    const [activeTab, setActiveTab] = useState(0);

    const previewMessage = invitationText
        ? replaceInvitationVariables(invitationText, testName)
        : 'No invitation text set yet.';

    const surveyLink = generateSurveyLink(poll?.id, testPhone);

    // Create poll object with prologue override if provided
    const pollWithPrologue = prologueOverride ? { ...poll, prologue: prologueOverride } : poll;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <HStack>
                        <Text>Preview Poll</Text>
                        <Badge colorScheme="purple">Preview Mode</Badge>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <Tabs index={activeTab} onChange={setActiveTab}>
                        <TabList>
                            <Tab>Invitation</Tab>
                            <Tab isDisabled={!questions || questions.length === 0}>
                                Survey {questions && questions.length > 0 ? `(${questions.length})` : ''}
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* Invitation Tab */}
                            <TabPanel>
                                <VStack spacing={4} align="stretch">
                                    <Text fontSize="sm" color="gray.600">
                                        Enter test data to see how the invitation will look with personalization:
                                    </Text>

                                    <HStack spacing={4}>
                                        <FormControl>
                                            <FormLabel fontSize="sm">Test Name</FormLabel>
                                            <Input
                                                value={testName}
                                                onChange={(e) => setTestName(e.target.value)}
                                                placeholder="John Smith"
                                                size="sm"
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel fontSize="sm">Test Phone</FormLabel>
                                            <Input
                                                value={testPhone}
                                                onChange={(e) => setTestPhone(e.target.value)}
                                                placeholder="+15551234567"
                                                size="sm"
                                            />
                                        </FormControl>
                                    </HStack>

                                    {/* SMS Preview */}
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold" mb={2}>
                                            SMS Preview:
                                        </Text>
                                        <Box
                                            p={4}
                                            bg="gray.50"
                                            borderRadius="lg"
                                            borderWidth="1px"
                                            borderColor="gray.300"
                                        >
                                            <VStack align="start" spacing={2}>
                                                <HStack w="full" justify="space-between">
                                                    <Text fontSize="xs" color="gray.500">
                                                        To: {testPhone}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        SMS
                                                    </Text>
                                                </HStack>
                                                <Box
                                                    w="full"
                                                    h="1px"
                                                    bg="gray.300"
                                                />
                                                <Box>
                                                    <Text fontSize="sm" whiteSpace="pre-wrap">
                                                        {previewMessage}
                                                    </Text>
                                                    {invitationText && (
                                                        <Text fontSize="sm" color="blue.600" mt={2}>
                                                            {surveyLink}
                                                        </Text>
                                                    )}
                                                </Box>
                                            </VStack>
                                        </Box>
                                    </Box>

                                    {/* Variable Reference */}
                                    <InfoMessage>
                                        <strong>Available variables:</strong> [firstname] - will be replaced with the respondent's first name
                                    </InfoMessage>
                                </VStack>
                            </TabPanel>

                            {/* Survey Tab */}
                            <TabPanel p={0}>
                                {questions && questions.length > 0 ? (
                                    <Box mt={4}>
                                        <SurveyPreview
                                            isOpen={activeTab === 1}
                                            onClose={() => {}}
                                            poll={pollWithPrologue}
                                            questions={questions}
                                        />
                                    </Box>
                                ) : (
                                    <Box p={8} textAlign="center">
                                        <Text color="gray.500">
                                            No questions added yet. Add questions to preview the survey experience.
                                        </Text>
                                    </Box>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default PreviewPollModal;
